import React, { useCallback, useMemo, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { _ } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import ConfirmModal from 'components/ConfirmModal'
import {
    GetSpacesQuery,
    GetSpacesQueryVariables,
    CreateNavigationItemMutation,
    CreateNavigationMenuMutation,
    UpdateNavigationItemMutation,
    UpdateNavigationMenuMutation,
    SpaceType,
} from 'generated/graphql'
import { useEventSlug, useQuery } from 'hooks'
import { getNodes } from 'graphql/utils'
import TextField from '../../forms/components/TextField'
import SelectField from '../../forms/components/SelectField'
import { spacesQuery } from '../graphql/query'
import {
    createNavigationItem, createNavigationMenu,
    deleteNavigationItem, deleteNavigationMenu,
    updateNavigationItem, updateNavigationMenu
} from '../graphql/mutation'
import { showNotification } from '../../notifications'
import { showErrorNotification } from '../../notifications/notificationsSlice'

type NavMenuEditorProps = {
    close: () => void
    onSubmitted: (data, isReplaced: boolean) => void
    newOrder?: number
    navMenu?: {
        id
        title: string
        order: number
        spaceId: number
        linkedSpaceId?: number
    }
}

interface NavMenuEditorModalProps {
    close: () => void
    isNew: boolean
}

const Validation = Yup.object().shape({
    title: Yup.string().required().min(3).max(50),
})

export const NavMenuEditorForm = ({ isSubMenu }: { isSubMenu?: boolean }) => {
    const eventSlug = useEventSlug()
    const [selectedSpace, setSelectedSpace] = useState<SpaceType>()
    const {
        data: spacesData,
        requestStatus
    } = useQuery<GetSpacesQuery, GetSpacesQueryVariables>(spacesQuery, {
        eventId: eventSlug,
        first: 499
    })
    const spaceTypeOptions = useMemo(() => {
        if (isSubMenu) {
            return getNodes(spacesData?.event?.spaces)
        }
        if (spacesData) {
            return [{
                name: _('navigation.form.space_not_linked'),
                id: -1,
            },
            ...getNodes(spacesData?.event?.spaces)
            ]
        }
        return []
    }, [isSubMenu, spacesData])

    if (requestStatus === 'loading') {
        return (
            <FullPageLoading />
        )
    }

    return (
        <>
            <TextField name="title" label={_('navigation.form.menu_title')} isRequired />
            <SelectField
                name="spaceId"
                label={_('navigation.form.internal_link')}
                options={spaceTypeOptions}
                optionValue="id"
                optionLabel="name"
                isRequired
                onSelect={(space) => setSelectedSpace(space)}
                defaultValue={spaceTypeOptions?.[0]}
            />
            {selectedSpace?.type === 'booth' && (
                <SelectField
                    name="linkedSpaceId"
                    label={_('navigation.form.linked_map')}
                    defaultValue={selectedSpace.linkedFrom?.[0]}
                    options={selectedSpace.linkedFrom}
                    optionValue="id"
                    optionLabel="name"
                />
            )}
        </>
    )
}

const NavMenuEditorModal = ({ close, isNew }: NavMenuEditorModalProps) => {
    const { submitForm, isValid } = useFormikContext()
    return (
        <>
            <ConfirmModal
                close={close}
                title={isNew ? _('navigation.create_dialog.title') : _('navigation.edit_dialog.title')}
                confirmDescription={<NavMenuEditorForm />}
                confirmText={isNew ? _('navigation.form.create') : _('navigation.form.edit')}
                disabledConfirm={!isValid}
                onBeforeConfirm={submitForm}
                variantConfirm="success"
            />
        </>
    )
}

const NavMenuEditor = ({
    close,
    onSubmitted,
    newOrder,
    navMenu,
}: NavMenuEditorProps) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()

    const isNew = !navMenu
    // Create or update
    const handleSubmit = useCallback(async ({ title, spaceId, linkedSpaceId }) => {
        let response
        let responseData
        let isReplaced = false
        if (!isNew) {
            // if change from navigation item to navigation menu, delete old and recreate the new one
            if (navMenu.spaceId === -1 && spaceId !== -1) {
                await deleteNavigationMenu(navMenu.id)
                response = await createNavigationItem({
                    eventId: eventSlug,
                    order: navMenu.order,
                    text: title,
                    spaceId,
                    linkedSpaceId,
                })
                responseData = (response.data as CreateNavigationItemMutation).createNavigationItem
                isReplaced = true
            } else if (navMenu.spaceId !== -1 && spaceId === -1) {
                await deleteNavigationItem(navMenu.id)
                response = await createNavigationMenu({
                    eventId: eventSlug,
                    order: navMenu.order,
                    text: title,
                })
                responseData = (response.data as CreateNavigationMenuMutation).createNavigationMenu
                isReplaced = true
            } else { // otherwise, update the existing one
                response = spaceId !== -1 ? await updateNavigationItem({
                    text: title,
                    spaceId,
                    linkedSpaceId,
                }, navMenu.id) : await updateNavigationMenu({
                    text: title,
                }, navMenu.id)

                responseData = spaceId !== -1
                    ? (response.data as UpdateNavigationItemMutation).updateNavigationItem
                    : (response.data as UpdateNavigationMenuMutation).updateNavigationMenu
            }
        } else {
            response = spaceId !== -1 ? await createNavigationItem({
                eventId: eventSlug,
                order: newOrder,
                text: title,
                spaceId,
                linkedSpaceId,
            }) : await createNavigationMenu({
                eventId: eventSlug,
                order: newOrder,
                text: title,
            })

            responseData = spaceId !== -1
                ? (response?.data as CreateNavigationItemMutation)?.createNavigationItem
                : (response?.data as CreateNavigationMenuMutation)?.createNavigationMenu
        }
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            onSubmitted && onSubmitted(responseData, isReplaced)
            return true
        }
        dispatch(showErrorNotification(response.errors))
        return false
    }, [dispatch, eventSlug, isNew, navMenu, newOrder, onSubmitted])

    return (
        <Formik
            initialValues={navMenu || { title: '', spaceId: '', linkedSpaceId: '' }}
            validateOnMount
            validationSchema={Validation}
            onSubmit={handleSubmit}
        >
            <NavMenuEditorModal close={close} isNew={isNew} />
        </Formik>
    )
}

export default NavMenuEditor
